import { useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer, DrawerBadge, Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { useAuth } from '@helios/auth-firebase-web';
import {
  canDeleteDeliveryOrder,
  qDeliveryOrderLinesByOrder,
  reversalWouldGoNegative,
  type DeliveryOrderLineRow,
  type DeliveryOrderRow,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { NavIcon } from '../../shell/shared';
import { SendEmailComingSoonModal } from './SendEmailComingSoonModal';

function formatNumber(value: number | null): string {
  if (value == null) return '—';
  return value.toLocaleString('en-US');
}

function statusBadgeVariant(status: string | null): 'warning' | 'neutral' {
  return status === 'draft' ? 'warning' : 'neutral';
}

function statusLabel(status: string | null): string {
  if (!status) return '—';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export function DeliveryOrderDetailsDrawer({
  open,
  onOpenChange,
  deliveryOrder,
  brandTokens,
  onViewPo,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deliveryOrder: DeliveryOrderRow | null;
  brandTokens: Record<string, string>;
  /** Optional callback to open the linked PO's details (stacked drawer). */
  onViewPo?: (purchaseOrderId: string) => void;
}) {
  const navigate = useNavigate();
  const { db } = usePowerSyncDb();
  const { user } = useAuth();

  const { data: lines = [] } = usePowerSyncQuery<DeliveryOrderLineRow>(
    qDeliveryOrderLinesByOrder(deliveryOrder?.id ?? ''),
  );

  const isDraft = deliveryOrder?.status === 'draft';
  const isDirect = deliveryOrder?.source_type === 'manual_direct_receipt';
  // Continue a draft: PO-based drafts resume on the receive page; direct
  // drafts resume on the direct-receipt page (PRD.265).
  const canContinue = isDraft && (deliveryOrder?.purchase_order_id != null || isDirect);
  const canDelete = canDeleteDeliveryOrder({ status: deliveryOrder?.status ?? null }).ok;

  // Send Email is a placeholder action — opens a "coming soon" modal
  // so users discover the planned capability. Wired up in a future PRD.
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const handleContinue = () => {
    if (!deliveryOrder) return;
    onOpenChange(false);
    if (deliveryOrder.purchase_order_id) {
      navigate(`/inventory/purchase-orders/${deliveryOrder.purchase_order_id}/receive`);
    } else {
      navigate(`/inventory/delivery-orders/direct/${deliveryOrder.id}`);
    }
  };

  const title = deliveryOrder?.do_number ?? 'Delivery Order';

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      portalStyle={brandTokens as CSSProperties}
      width="min(720px, 60vw)"
      badges={
        deliveryOrder ? (
          <DrawerBadge
            label={statusLabel(deliveryOrder.status)}
            variant={statusBadgeVariant(deliveryOrder.status)}
          />
        ) : null
      }
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <Button variant="outline" size="lg" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <IconButton
              ariaLabel="Send DO via email"
              title="Send via email"
              onClick={() => setEmailModalOpen(true)}
              disabled={!deliveryOrder}
            >
              <NavIcon name="mail" />
            </IconButton>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            {canDelete && (
              <Button
                variant="outline"
                size="lg"
                onClick={() => setCancelModalOpen(true)}
                style={{ borderColor: '#fecaca', color: '#b91c1c' }}
              >
                Delete
              </Button>
            )}
            {canContinue && (
              <Button variant="gradient" size="lg" onClick={handleContinue}>
                Continue Receiving
              </Button>
            )}
          </div>
        </div>
      }
    >
      <SendEmailComingSoonModal
        open={emailModalOpen}
        onOpenChange={setEmailModalOpen}
        documentLabel="Delivery Order"
        documentNumber={deliveryOrder?.do_number ?? null}
        portalStyle={brandTokens as CSSProperties}
      />
      {deliveryOrder && (
        <CancelReverseModal
          open={cancelModalOpen}
          onOpenChange={setCancelModalOpen}
          deliveryOrder={deliveryOrder}
          lines={lines}
          isDirect={isDirect}
          db={db}
          userId={user?.uid ?? ''}
          userName={user?.displayName ?? user?.email ?? ''}
          portalStyle={brandTokens as CSSProperties}
          onReversed={() => {
            setCancelModalOpen(false);
            onOpenChange(false);
          }}
        />
      )}
      {!deliveryOrder ? (
        <div
          style={{
            padding: 'var(--sp-6)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          Loading…
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-6)',
            padding: 'var(--sp-5) var(--sp-6)',
          }}
        >
          <HeaderInfo deliveryOrder={deliveryOrder} onViewPo={onViewPo} />
          <LinesSection lines={lines} amountsAre={deliveryOrder.amounts_are} />
          <TotalsSection
            subtotal={deliveryOrder.subtotal}
            taxAmount={deliveryOrder.tax_amount}
            invoiceTotal={deliveryOrder.invoice_total}
            amountsAre={deliveryOrder.amounts_are}
          />
        </div>
      )}
    </Drawer>
  );
}

/**
 * PRD.265 §11 — Delete (soft-cancel) a Delivery Order.
 *
 * A draft carries no stock, so it is simply marked cancelled. A finalized
 * (received) DO posts a compensating outbound stock movement (quantity_out =
 * quantity received + bonus) for every line before marking cancelled — blocked
 * when reversing any line would drive that item's (or batch's) available stock
 * negative, i.e. the received stock was already consumed downstream.
 */
function CancelReverseModal({
  open,
  onOpenChange,
  deliveryOrder,
  lines,
  isDirect,
  db,
  userId,
  userName,
  portalStyle,
  onReversed,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deliveryOrder: DeliveryOrderRow;
  lines: DeliveryOrderLineRow[];
  isDirect: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  db: any;
  userId: string;
  userName: string;
  portalStyle: CSSProperties;
  onReversed: () => void;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  async function handleConfirm() {
    setError(null);
    if (!db) return setError('Local database not ready.');

    setWorking(true);
    try {
      // Re-check status to avoid a double delete/reversal from a stale drawer.
      const current = (await db.getOptional(
        `SELECT status FROM delivery_orders WHERE id = ? LIMIT 1`,
        [deliveryOrder.id],
      )) as { status: string | null } | null;
      if (current?.status !== 'draft' && current?.status !== 'received') {
        setWorking(false);
        return setError('This delivery order can no longer be deleted.');
      }
      const isReceived = current.status === 'received';

      // A finalized delivery moved stock, so a reason is required for the audit
      // trail; a draft never touched stock and can be removed without one.
      if (isReceived && !reason.trim()) {
        setWorking(false);
        return setError('Enter a reason for reversing this received delivery.');
      }

      const now = new Date().toISOString();

      if (isReceived) {
        // 1) Availability guard — block if any line's stock was already consumed.
        const blockers: string[] = [];
        for (const line of lines) {
          if (!line.inventory_item_id) continue;
          const reverseQty =
            (Number(line.quantity_received) || 0) + (Number(line.bonus_quantity_received) || 0);
          if (reverseQty <= 0) continue;
          const params: unknown[] = [line.inventory_item_id];
          let batchClause = '';
          if (line.inventory_batch_id) {
            batchClause = ' AND inventory_batch_id = ?';
            params.push(line.inventory_batch_id);
          }
          const row = (await db.getOptional(
            `SELECT COALESCE(SUM(quantity_in), 0) - COALESCE(SUM(quantity_out), 0) AS available
             FROM stock_movements
             WHERE inventory_item_id = ?${batchClause}`,
            params,
          )) as { available: number | null } | null;
          const available = Number(row?.available) || 0;
          if (reversalWouldGoNegative(available, reverseQty)) {
            blockers.push(
              `${line.item_name_snapshot ?? 'Item'} (available ${available}, need ${reverseQty})`,
            );
          }
        }
        if (blockers.length > 0) {
          setWorking(false);
          return setError(
            `Cannot delete — received stock has already been used:\n${blockers.join('\n')}`,
          );
        }

        // 2) Compensating outbound movements.
        const movementDate = now.slice(0, 10);
        const movementType = isDirect ? 'direct_delivery_reversal' : 'delivery_reversal';
        for (const line of lines) {
          if (!line.inventory_item_id) continue;
          const reverseQty =
            (Number(line.quantity_received) || 0) + (Number(line.bonus_quantity_received) || 0);
          if (reverseQty <= 0) continue;
          await db.execute(
            `INSERT INTO stock_movements (
              id, org_id, inventory_item_id, inventory_batch_id,
              source_type, source_id, source_line_id,
              movement_type, quantity_in, quantity_out, unit,
              movement_date, remarks, created_by, created_at, updated_at
            ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              crypto.randomUUID(),
              deliveryOrder.org_id,
              line.inventory_item_id,
              line.inventory_batch_id,
              'delivery_order',
              deliveryOrder.id,
              line.id,
              movementType,
              0,
              reverseQty,
              line.unit_snapshot,
              movementDate,
              `Reversal: ${reason.trim()}`,
              userId || null,
              now,
              now,
            ],
          );
        }
      }

      // 3) Mark cancelled (both draft and received).
      await db.execute(
        `UPDATE delivery_orders SET
          status = 'cancelled', cancelled_at = ?, cancelled_by = ?, cancel_reason = ?, updated_at = ?
         WHERE id = ?`,
        [now, userName || userId || null, reason.trim() || null, now, deliveryOrder.id],
      );

      onReversed();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed. Please try again.';
      setError(msg);
      setWorking(false);
    }
  }

  const isReceived = deliveryOrder.status === 'received';
  const doLabel = deliveryOrder.do_number ?? deliveryOrder.id.slice(0, 8);
  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onOpenChange(false)}
      title="Delete Delivery Order"
      maxWidth={520}
      portalStyle={portalStyle}
      footer={
        <div style={{ display: 'flex', gap: 'var(--sp-3)', marginLeft: 'auto' }}>
          <Button
            variant="outline"
            size="md"
            onClick={() => onOpenChange(false)}
            disabled={working}
          >
            Keep Delivery
          </Button>
          <Button
            variant="gradient"
            size="md"
            onClick={handleConfirm}
            disabled={working}
            style={{ background: '#dc2626' }}
          >
            {working ? 'Deleting…' : 'Delete Delivery'}
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-4) var(--sp-5) var(--sp-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4)',
        }}
      >
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          {isReceived ? (
            <>
              This posts compensating outbound stock movements for every received line and marks{' '}
              <strong>{doLabel}</strong> as cancelled. It is blocked if any received stock has
              already been consumed.
            </>
          ) : (
            <>
              This marks the draft <strong>{doLabel}</strong> as cancelled. It carries no stock, so
              nothing is reversed.
            </>
          )}
        </p>
        {error && (
          <div
            role="alert"
            style={{
              background: '#fee2e2',
              color: '#991b1b',
              border: '1px solid #fecaca',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--sp-3) var(--sp-4)',
              fontSize: 'var(--font-size-sm)',
              whiteSpace: 'pre-wrap',
            }}
          >
            {error}
          </div>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              color: 'var(--text)',
            }}
          >
            Reason {isReceived && <span style={{ color: '#dc2626' }}>*</span>}
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g., Received in error / wrong supplier invoice"
            style={{
              padding: '8px 10px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text)',
              fontFamily: 'var(--font-family)',
              resize: 'vertical',
            }}
          />
        </label>
      </div>
    </Modal>
  );
}

function IconButton({
  ariaLabel,
  title,
  onClick,
  disabled = false,
  children,
}: {
  ariaLabel: string;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      title={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 40,
        height: 40,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: disabled ? 'var(--text-muted)' : 'var(--text)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        padding: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function HeaderInfo({
  deliveryOrder,
  onViewPo,
}: {
  deliveryOrder: DeliveryOrderRow;
  onViewPo?: (purchaseOrderId: string) => void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-4)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-surface-2)',
      }}
    >
      <Field label="Supplier" value={deliveryOrder.supplier_name ?? '—'} />
      <Field label="Supplier Invoice #" value={deliveryOrder.supplier_invoice_number ?? '—'} />
      <Field label="Reference" value={deliveryOrder.reference ?? '—'} />
      <Field label="Date Raised" value={deliveryOrder.date_raised ?? '—'} />
      <Field label="Delivery Date" value={deliveryOrder.delivery_date ?? '—'} />
      <Field label="Received Date" value={deliveryOrder.received_date ?? '—'} />
      <Field
        label="Amounts"
        value={deliveryOrder.amounts_are === 'tax_inclusive' ? 'Tax inclusive' : 'Tax exclusive'}
      />
      <LinkedPoField purchaseOrderId={deliveryOrder.purchase_order_id} onViewPo={onViewPo} />
      <Field
        label="Source"
        value={
          deliveryOrder.source_type === 'manual_direct_receipt'
            ? 'Direct Delivery Receipt'
            : 'From Purchase Order'
        }
      />
      {deliveryOrder.notes && (
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Notes" value={deliveryOrder.notes} multiline />
        </div>
      )}
      {deliveryOrder.status === 'cancelled' && (
        <div style={{ gridColumn: '1 / -1' }}>
          <Field
            label="Cancellation"
            value={
              [
                deliveryOrder.cancel_reason,
                deliveryOrder.cancelled_at ? `on ${deliveryOrder.cancelled_at.slice(0, 10)}` : null,
              ]
                .filter(Boolean)
                .join(' · ') || 'Reversed'
            }
            multiline
          />
        </div>
      )}
    </div>
  );
}

function LinkedPoField({
  purchaseOrderId,
  onViewPo,
}: {
  purchaseOrderId: string | null;
  onViewPo?: (purchaseOrderId: string) => void;
}) {
  if (!purchaseOrderId) {
    return <Field label="Linked PO" value="—" />;
  }
  if (!onViewPo) {
    return <Field label="Linked PO" value="Yes" />;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
        }}
      >
        Linked PO
      </span>
      <button
        type="button"
        onClick={() => onViewPo(purchaseOrderId)}
        style={{
          alignSelf: 'flex-start',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          height: 28,
          padding: '0 10px',
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--color-primary)',
          background: 'transparent',
          color: 'var(--color-primary)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-medium)',
          cursor: 'pointer',
        }}
      >
        View PO
        <span aria-hidden style={{ fontSize: 12, lineHeight: 1 }}>
          →
        </span>
      </button>
    </div>
  );
}

function Field({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text)',
          whiteSpace: multiline ? 'pre-wrap' : 'normal',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text)',
      }}
    >
      {children}
    </span>
  );
}

const LINE_COLUMNS = '1.6fr 60px 70px 70px 90px 100px';

function LinesSection({
  lines,
  amountsAre,
}: {
  lines: DeliveryOrderLineRow[];
  amountsAre: string | null;
}) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      <SectionLabel>
        Line Items{' '}
        <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text-muted)' }}>
          ({lines.length}){amountsAre === 'tax_inclusive' ? ' · Tax inclusive' : ''}
        </span>
      </SectionLabel>

      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: LINE_COLUMNS,
            gap: 'var(--sp-3)',
            padding: 'var(--sp-2) var(--sp-3)',
            background: 'var(--bg-surface-2)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
          }}
        >
          <span>Item</span>
          <span style={{ textAlign: 'right' }}>Ord</span>
          <span style={{ textAlign: 'right' }}>Recv</span>
          <span style={{ textAlign: 'right' }}>Bonus</span>
          <span style={{ textAlign: 'right' }}>Rate</span>
          <span style={{ textAlign: 'right' }}>Amount</span>
        </div>

        {lines.length === 0 ? (
          <div
            style={{
              padding: 'var(--sp-4) var(--sp-3)',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              textAlign: 'center',
            }}
          >
            No line items.
          </div>
        ) : (
          lines.map((l) => <LineRow key={l.id} line={l} />)
        )}
      </div>
    </section>
  );
}

function LineRow({ line }: { line: DeliveryOrderLineRow }) {
  const fmt = useFormatMoney();
  const batchInfo = [line.batch_number_snapshot, line.expiry_date_snapshot]
    .filter(Boolean)
    .join(' · exp ');
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: LINE_COLUMNS,
        gap: 'var(--sp-3)',
        alignItems: 'flex-start',
        padding: 'var(--sp-3)',
        borderTop: '1px solid var(--border)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text)',
            fontWeight: 'var(--font-weight-medium)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {line.item_name_snapshot ?? '—'}
        </span>
        {(line.sku_snapshot || line.unit_snapshot) && (
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            {[line.sku_snapshot, line.unit_snapshot].filter(Boolean).join(' · ')}
          </span>
        )}
        {batchInfo && (
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            {batchInfo}
          </span>
        )}
        {line.remarks && (
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            {line.remarks}
          </span>
        )}
      </div>
      <span style={numCellStyle}>{formatNumber(line.quantity_ordered)}</span>
      <span style={numCellStyle}>{formatNumber(line.quantity_received)}</span>
      <span style={numCellStyle}>{formatNumber(line.bonus_quantity_received)}</span>
      <span style={numCellStyle}>{line.rate == null ? '—' : fmt(line.rate)}</span>
      <span style={{ ...numCellStyle, fontWeight: 'var(--font-weight-semi)' }}>
        {line.amount == null ? '—' : fmt(line.amount)}
      </span>
    </div>
  );
}

const numCellStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

function TotalsSection({
  subtotal,
  taxAmount,
  invoiceTotal,
  amountsAre,
}: {
  subtotal: number | null;
  taxAmount: number | null;
  invoiceTotal: number | null;
  amountsAre: string | null;
}) {
  return (
    <section
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
        padding: 'var(--sp-3) var(--sp-4)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--bg-surface-2)',
      }}
    >
      <TotalRow label="Subtotal" value={subtotal} muted />
      {taxAmount != null && taxAmount > 0 && <TotalRow label="Tax" value={taxAmount} muted />}
      <TotalRow
        label={`Invoice Total ${amountsAre === 'tax_inclusive' ? '(incl. tax)' : '(excl. tax)'}`}
        value={invoiceTotal}
      />
    </section>
  );
}

function TotalRow({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: number | null;
  muted?: boolean;
}) {
  const fmt = useFormatMoney();
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: muted ? 'var(--font-size-sm)' : 'var(--font-size-lg)',
          fontWeight: muted ? 'var(--font-weight-medium)' : 'var(--font-weight-semi)',
          color: muted ? 'var(--text-muted)' : 'var(--text)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value == null ? '—' : fmt(value)}
      </span>
    </div>
  );
}
